/**
 * Domain Filter Definition
 *
 * Filters nodes by their app domain (BhajanMala, RagaSagara, SgsSatsang)
 */

import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { GraphDomain, GraphDataNode } from '../../types/graph.types';
import {
  FilterDefinition,
  FilterOperator,
  FilterOperatorLabels,
  FilterValueType,
  FilterSuggestion,
  FilterValue,
  FilterConfigProps,
} from '../types';

/**
 * Domain display names and aliases for matching
 */
const domainInfo: Record<
  number,
  { name: string; shortName: string; aliases: string[] }
> = {
  [GraphDomain.BhajanMala]: {
    name: 'Bhajan Mala',
    shortName: 'BM',
    aliases: ['bhajan', 'bhajanmala', 'bm', 'mala'],
  },
  [GraphDomain.RagaSagara]: {
    name: 'Raga Sagara',
    shortName: 'RS',
    aliases: ['raga', 'ragasagara', 'rs', 'sagara'],
  },
  [GraphDomain.SgsSatsang]: {
    name: 'SGS Satsang',
    shortName: 'SS',
    aliases: ['sgs', 'satsang', 'ss', 'sgssatsang'],
  },
};

/**
 * Get domain options for the filter
 */
const domainOptions = Object.entries(domainInfo).map(([id, info]) => ({
  value: parseInt(id, 10) as GraphDomain,
  label: info.name,
}));

/**
 * Domain Filter Config Component - supports multi-select for domains
 */
const DomainFilterConfig: React.FC<FilterConfigProps> = ({
  filter,
  definition,
  onUpdate,
  onClose,
}) => {
  const [operator, setOperator] = useState<FilterOperator>(filter.operator);
  const [selectedDomains, setSelectedDomains] = useState<GraphDomain[]>(() => {
    const value = filter.value;
    if (Array.isArray(value)) {
      return value as GraphDomain[];
    }
    return value ? [value as GraphDomain] : [];
  });

  const dialogRef = useRef<HTMLDivElement>(null);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const toggleDomain = (domain: GraphDomain) => {
    setSelectedDomains((prev) =>
      prev.includes(domain)
        ? prev.filter((d) => d !== domain)
        : [...prev, domain]
    );
  };

  const handleApply = () => {
    if (selectedDomains.length === 0) {
      return;
    }
    // Always store as array for consistency
    onUpdate(operator, selectedDomains);
  };

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
          {definition.label}
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="
            p-1 rounded-full
            text-gray-400 hover:text-gray-500
            dark:text-gray-500 dark:hover:text-gray-400
            hover:bg-gray-100 dark:hover:bg-gray-800
            transition-colors
          "
          aria-label="Close"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Body */}
      <div className="px-4 py-4 space-y-4">
        {/* Operator */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Condition
          </label>
          <div className="flex flex-wrap gap-2">
            {definition.supportedOperators.map((op) => (
              <button
                key={op}
                type="button"
                onClick={() => setOperator(op)}
                className={`
                  px-3 py-1.5 text-sm rounded-md
                  border transition-colors
                  ${operator === op
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }
                `}
              >
                {FilterOperatorLabels[op]}
              </button>
            ))}
          </div>
        </div>

        {/* Domain Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Select Apps (can select multiple)
          </label>
          <div className="space-y-2">
            {Object.entries(domainInfo).map(([domainId, info]) => {
              const domain = parseInt(domainId, 10) as GraphDomain;
              const isSelected = selectedDomains.includes(domain);
              return (
                <label
                  key={domainId}
                  className={`
                    flex items-center gap-3 p-3 rounded-lg cursor-pointer
                    border transition-colors
                    ${isSelected
                      ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700'
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }
                  `}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleDomain(domain)}
                    className="
                      h-4 w-4 rounded
                      text-blue-600
                      border-gray-300 dark:border-gray-600
                      focus:ring-blue-500
                    "
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {info.name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {info.shortName}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-3 px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <button
          type="button"
          onClick={onClose}
          className="
            px-4 py-2 text-sm font-medium
            text-gray-700 dark:text-gray-300
            bg-white dark:bg-gray-800
            border border-gray-300 dark:border-gray-600
            rounded-md
            hover:bg-gray-50 dark:hover:bg-gray-700
            focus:outline-none focus:ring-2 focus:ring-blue-500
            transition-colors
          "
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleApply}
          disabled={selectedDomains.length === 0}
          className="
            px-4 py-2 text-sm font-medium
            text-white
            bg-blue-600 hover:bg-blue-700
            disabled:bg-blue-400 disabled:cursor-not-allowed
            rounded-md
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
            transition-colors
          "
        >
          Apply
        </button>
      </div>
    </>
  );
};

/**
 * Domain Filter Definition
 */
export const domainFilter: FilterDefinition = {
  id: 'domain',
  label: 'App Domain',
  shortLabel: 'App',
  icon: 'GlobeAltIcon',
  valueType: FilterValueType.Domain,
  supportedOperators: [
    FilterOperator.Contains,
    FilterOperator.NotContains,
  ],
  defaultOperator: FilterOperator.Contains,
  options: domainOptions,
  placeholder: 'Select app domain...',
  ConfigComponent: DomainFilterConfig,

  suggestMatch(query: string, _nodes: GraphDataNode[]): FilterSuggestion[] {
    const normalizedQuery = query.toLowerCase().trim();
    if (!normalizedQuery) {
      return [];
    }

    const suggestions: FilterSuggestion[] = [];

    // Check each domain for matches
    for (const [domainId, info] of Object.entries(domainInfo)) {
      const domain = parseInt(domainId, 10) as GraphDomain;

      // Check if query matches name, short name, or aliases
      const allTerms = [
        info.name.toLowerCase(),
        info.shortName.toLowerCase(),
        ...info.aliases,
      ];

      let matchScore = 0;

      for (const term of allTerms) {
        if (term === normalizedQuery) {
          matchScore = 100; // Exact match
          break;
        } else if (term.startsWith(normalizedQuery)) {
          matchScore = Math.max(matchScore, 80);
        } else if (term.includes(normalizedQuery)) {
          matchScore = Math.max(matchScore, 50);
        }
      }

      if (matchScore > 0) {
        suggestions.push({
          filterId: 'domain',
          label: `App: ${info.name}`,
          description: `Show nodes from ${info.name}`,
          prefilledOperator: FilterOperator.Contains,
          prefilledValue: domain,
          needsConfiguration: true,
          score: matchScore,
        });
      }
    }

    // Check for "app" keyword
    if ('app'.startsWith(normalizedQuery) || normalizedQuery.includes('app')) {
      // If just typing "app", show a generic app filter suggestion
      if (suggestions.length === 0) {
        suggestions.push({
          filterId: 'domain',
          label: 'App: ...',
          description: 'Filter by application domain',
          prefilledOperator: FilterOperator.Contains,
          prefilledValue: GraphDomain.BhajanMala,
          needsConfiguration: true,
          score: 40,
        });
      }
    }

    return suggestions.sort((a, b) => b.score - a.score);
  },

  execute(node: GraphDataNode, operator: FilterOperator, value: FilterValue): boolean {
    // The serverless API exposes a numeric `domains` mask; older shapes used
    // a singular `domain`. Default to 0 when neither is present.
    const nodeDomain = node.domains ?? node.domain ?? 0;

    // Handle array values for Contains/NotContains operators
    if (Array.isArray(value)) {
      const domains = value as GraphDomain[];

      switch (operator) {
        case FilterOperator.Contains:
          // Node must be in at least one of the selected domains (bitwise OR logic)
          return domains.some((domain) => (nodeDomain & domain) !== 0);

        case FilterOperator.NotContains:
          // Node must not be in any of the selected domains
          return !domains.some((domain) => (nodeDomain & domain) !== 0);

        default:
          return true;
      }
    }

    // Handle single domain value
    const filterDomain = value as GraphDomain;

    switch (operator) {
      case FilterOperator.Contains:
        // Bitwise check: node belongs to this domain
        return (nodeDomain & filterDomain) !== 0;

      case FilterOperator.NotContains:
        // Node does NOT belong to this domain
        return (nodeDomain & filterDomain) === 0;

      default:
        return true;
    }
  },

  getDisplayValue(value: FilterValue): string {
    if (Array.isArray(value)) {
      return (value as GraphDomain[])
        .map((d) => domainInfo[d]?.name || `Domain ${d}`)
        .join(', ');
    }

    const domain = value as GraphDomain;
    return domainInfo[domain]?.name || `Domain ${domain}`;
  },
};
